import { useCallback, useEffect, useState } from 'react';
import { api, type FinanceReport, type InvoiceRow, type PatientSummary, type TreatmentView } from '../api';
import { useI18n } from '../i18n';
import { PatientPicker } from '../components/PatientPicker';
import { EmptyState, ErrorState, Field, Modal, Skeleton, Spinner, Tabs } from '../components';
import { formatMinor, minorToInput, parseToMinor } from '../money';
import { ProcedureReport } from './ProcedureReport';
import { PeriodPicker, initialPeriod, type Period } from '../components/PeriodPicker';
import { OFFERED_PAYMENT_METHODS } from '@oxyflows/contracts';
import type { Payables } from '../api-modules';
import { ConfirmRemove } from '../components/ConfirmRemove';
import { describe } from '../errors';

type Tab = 'reports' | 'procedures' | 'unbilled' | 'invoices' | 'payments' | 'payables' | 'expenses';

export function Finance({
  clinicId,
  permissions,
  currency,
}: {
  clinicId: string;
  permissions: string[];
  /** The clinic's currency: every figure without a document of its own is in it. */
  currency: string;
}) {
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>('reports');
  const can = (p: string) => permissions.includes(p);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t.finance.title}</h1>
        </div>
      </div>

      <Tabs
        value={tab}
        onChange={(v) => setTab(v as Tab)}
        tabs={[
          { value: 'reports', label: t.finance.reports, hidden: !can('finance.reports.read') },
          { value: 'procedures', label: t.reports.procedures, hidden: !can('finance.reports.read') },
          { value: 'unbilled', label: t.finance.unbilled, hidden: !can('finance.invoice.create') },
          { value: 'invoices', label: t.finance.invoices },
          { value: 'payments', label: t.finance.payments },
          { value: 'payables', label: t.finance.payables, hidden: !can('finance.reports.read') },
          { value: 'expenses', label: t.finance.expenses, hidden: !can('finance.expense.manage') },
        ]}
      />

      {tab === 'unbilled' ? (
        <Unbilled clinicId={clinicId} currency={currency} onInvoiced={() => setTab('invoices')} />
      ) : null}
      {tab === 'payables' ? (
        <PayablesPanel clinicId={clinicId} currency={currency} canPay={can('finance.payment.record')} />
      ) : null}

      {tab === 'reports' ? <Reports clinicId={clinicId} /> : null}
      {tab === 'procedures' ? <ProcedureReport clinicId={clinicId} /> : null}
      {tab === 'invoices' ? (
        <Invoices
          clinicId={clinicId}
          canPost={can('finance.invoice.post')}
          canCreate={can('finance.invoice.create')}
          canPay={can('finance.payment.record')}
          canDelete={can('finance.invoice.delete')}
        />
      ) : null}
      {tab === 'payments' ? (
        <Payments
          clinicId={clinicId}
          canRecord={can('finance.payment.record')}
          canVoid={can('finance.payment.void')}
        />
      ) : null}
      {tab === 'expenses' ? <Expenses clinicId={clinicId} currency={currency} /> : null}
    </>
  );
}

/**
 * Reporting.
 *
 * Every tile is labelled for what it actually measures and carries its own
 * one-line explanation. "Billed" is not "collected" is not "outstanding", and
 * the profit figure says out loud that it is an estimate (spec §10).
 */
function Reports({ clinicId }: { clinicId: string }) {
  const { t, locale, formatNumber } = useI18n();
  const [period, setPeriod] = useState<Period>(() => initialPeriod('quarter'));
  const [data, setData] = useState<FinanceReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    api.financeReports(period.from, period.to).then(setData).catch((e) => setError(describe(e, t)));
  }, [clinicId, period, t]);

  if (error) return <ErrorState message={error} />;
  if (!data) return <div className="card"><Skeleton rows={6} /></div>;

  const f = data.figures;
  const profit = f.collectedMinor - f.expensesMinor - f.labCostMinor - f.commissionMinor;
  const money = (v: number) => formatMinor(v, data.currency, locale);

  const agingLabels: Record<string, string> = {
    current: t.finance.current,
    d1_30: '1–30',
    d31_60: '31–60',
    d61_90: '61–90',
    d90_plus: '90+',
  };

  return (
    <div className="stack">
      {/* Every figure below is for the period named here, so the period is
          chosen at the top rather than implied by a default nobody set. */}
      <div className="toolbar" style={{ marginBottom: 0 }}>
        <PeriodPicker value={period} onChange={setPeriod} />
      </div>

      <div className="stats">
        <Stat label={t.finance.billed} hint={t.finance.billedHint} value={money(f.billedMinor)} tone="blue" />
        <Stat label={t.finance.collected} hint={t.finance.collectedHint} value={money(f.collectedMinor)} tone="teal" />
        <Stat
          label={t.finance.outstanding}
          hint={t.finance.outstandingHint}
          value={money(f.outstandingMinor)}
          tone="amber"
        />
        <Stat label={t.finance.expenses} hint={t.finance.expensesHint} value={money(f.expensesMinor)} tone="violet" />
      </div>

      <div className="grid-2" style={{ alignItems: 'start' }}>
        <div className="card">
          <div className="card-head">
            <h2>{t.finance.profitEstimate}</h2>
          </div>
          <div className="card-body">
            <div className="stat-value" style={{ color: profit >= 0 ? 'var(--ok)' : 'var(--danger)' }}>
              {money(profit)}
            </div>
            <p className="hint" style={{ color: 'var(--ink-3)', fontSize: 12.5, marginTop: 8 }}>
              {t.finance.profitHint}
            </p>
            <dl style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '6px 16px', marginTop: 14 }}>
              <dt style={{ color: 'var(--ink-2)' }}>{t.finance.collected}</dt>
              <dd style={{ margin: 0 }} className="ltr-value">{money(f.collectedMinor)}</dd>
              <dt style={{ color: 'var(--ink-2)' }}>− {t.finance.expenses}</dt>
              <dd style={{ margin: 0 }} className="ltr-value">{money(f.expensesMinor)}</dd>
              <dt style={{ color: 'var(--ink-2)' }}>− {t.finance.labCost}</dt>
              <dd style={{ margin: 0 }} className="ltr-value">{money(f.labCostMinor)}</dd>
              <dt style={{ color: 'var(--ink-2)' }}>− {t.finance.commission}</dt>
              <dd style={{ margin: 0 }} className="ltr-value">{money(f.commissionMinor)}</dd>
            </dl>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>{t.finance.aging}</h2>
          </div>
          <div className="card-body stack" style={{ gap: 10 }}>
            {Object.entries(data.aging).map(([bucket, amount]) => {
              const max = Math.max(...Object.values(data.aging), 1);
              return (
                <div key={bucket}>
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <span>{agingLabels[bucket] ?? bucket}</span>
                    <span className="ltr-value">{money(amount)}</span>
                  </div>
                  <div className="bar">
                    <div
                      className={`bar-fill${bucket === 'd90_plus' ? ' danger' : bucket === 'current' ? ' ok' : ''}`}
                      style={{ width: `${(amount / max) * 100}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Money out, by who received it — the question "paid out" as a
          single figure never answers. */}
      <div className="card">
        <div className="card-head">
          <h2>{t.finance.paidOut}</h2>
        </div>
        <div className="card-body stack" style={{ gap: 14 }}>
          <p style={{ color: 'var(--ink-3)', margin: 0, fontSize: 13 }}>{t.finance.paidOutHint}</p>
          {(
            [
              [t.finance.toDentists, data.paidOut.dentists.map((d) => ({ name: d.name, amountMinor: d.amountMinor }))],
              [t.finance.toSuppliers, data.paidOut.suppliers.map((d) => ({ name: d.name, amountMinor: d.amountMinor }))],
              [t.finance.toLaboratories, data.paidOut.laboratories.map((d) => ({ name: d.name, amountMinor: d.amountMinor }))],
              [t.finance.expensesByCategory, data.paidOut.expensesByCategory.map((d) => ({ name: d.category, amountMinor: d.amountMinor }))],
            ] as [string, { name: string; amountMinor: number }[]][]
          ).map(([label, list]) =>
            list.length === 0 ? null : (
              <div key={label}>
                <div className="section-label">{label}</div>
                <dl className="detail-list" style={{ marginTop: 6 }}>
                  {list.map((row) => (
                    <div key={row.name}>
                      <dt>{row.name}</dt>
                      <dd style={{ margin: 0 }} className="ltr-value">{money(row.amountMinor)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ),
          )}
          {data.paidOut.patientRefundsMinor > 0 || data.paidOut.otherMinor > 0 ? (
            <dl className="detail-list">
              {data.paidOut.patientRefundsMinor > 0 ? (
                <div><dt>{t.finance.refunds}</dt><dd style={{ margin: 0 }} className="ltr-value">{money(data.paidOut.patientRefundsMinor)}</dd></div>
              ) : null}
              {data.paidOut.otherMinor > 0 ? (
                <div><dt>{t.finance.otherOut}</dt><dd style={{ margin: 0 }} className="ltr-value">{money(data.paidOut.otherMinor)}</dd></div>
              ) : null}
            </dl>
          ) : null}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>{t.finance.byDentist}</h2>
        </div>
        {data.byDentist.length === 0 ? (
          <div className="empty">{t.dentists.noAccruals}</div>
        ) : (
          <div className="card-body flush table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t.appointments.dentist}</th>
                  {/* Counted distinctly, and named so they cannot be confused. */}
                  <th>{t.finance.uniquePatients}</th>
                  <th>{t.finance.uniqueTeeth}</th>
                  <th>{t.finance.procedures}</th>
                  <th>{t.finance.produced}</th>
                </tr>
              </thead>
              <tbody>
                {data.byDentist.map((d) => (
                  <tr key={d.membershipId}>
                    <td><strong>{d.name}</strong></td>
                    <td>{formatNumber(d.uniquePatients)}</td>
                    <td>{formatNumber(d.uniqueTeeth)}</td>
                    <td>{formatNumber(d.procedures)}</td>
                    <td className="ltr-value">{money(d.producedMinor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-head">
          <h2>{t.finance.byCategory}</h2>
        </div>
        <div className="card-body flush table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t.finance.category}</th>
                <th>{t.finance.procedures}</th>
                <th>{t.finance.produced}</th>
              </tr>
            </thead>
            <tbody>
              {data.byCategory.map((c) => (
                <tr key={c.category}>
                  <td>{t.finance.expenseCategory[c.category] ?? c.category}</td>
                  <td>{formatNumber(c.procedures)}</td>
                  <td className="ltr-value">{money(c.producedMinor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/**
 * One measured figure.
 *
 * Label first, then the number, then what the number actually counts — the
 * same order as every other tile in the application, so a row of them scans
 * as one thing. `tone` only tints the rule along the top; the hint is what
 * distinguishes billed from collected from outstanding, not the colour.
 */
function Stat({
  label,
  hint,
  value,
  tone = 'teal',
}: {
  label: string;
  hint: string;
  value: string;
  tone?: string;
}) {
  return (
    <div className="card stat">
      <div className="stat-top">
        <span>{label}</span>
        <span className={`stat-icon ${tone}`} aria-hidden="true">●</span>
      </div>
      <div className="stat-value ltr-value" style={{ fontSize: 24 }}>{value}</div>
      <div className="stat-hint">{hint}</div>
    </div>
  );
}

function Invoices({
  clinicId,
  canPost,
  canCreate,
  canPay,
  canDelete,
}: {
  clinicId: string;
  canPost: boolean;
  canCreate: boolean;
  canPay: boolean;
  canDelete: boolean;
}) {
  const { t, locale, formatDate, personName } = useI18n();
  const [rows, setRows] = useState<InvoiceRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [paying, setPaying] = useState<InvoiceRow | null>(null);
  const [discarding, setDiscarding] = useState<InvoiceRow | null>(null);
  const [nonce, setNonce] = useState(0);

  const load = useCallback(async () => {
    try {
      const r = await api.invoices();
      setRows(r.invoices);
    } catch (e) {
      setError(describe(e, t));
    }
  }, [t]);

  useEffect(() => {
    setRows(null);
    void load();
  }, [clinicId, load, nonce]);

  async function post(id: string) {
    setBusyId(id);
    try {
      await api.postInvoice(id);
      setNonce((n) => n + 1);
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusyId(null);
    }
  }

  if (error) return <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} />;

  return (
    <>
      <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 12 }}>
        {canCreate ? (
          <button type="button" className="btn" onClick={() => setCreating(true)}>
            + {t.finance.newInvoice}
          </button>
        ) : null}
      </div>

      <div className="card">
        {rows === null ? (
          <Skeleton rows={5} />
        ) : rows.length === 0 ? (
          <EmptyState title={t.finance.noInvoices} />
        ) : (
          <div className="card-body flush table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t.finance.number}</th>
                  <th>{t.finance.patient}</th>
                  <th>{t.finance.issued}</th>
                  <th>{t.finance.total}</th>
                  <th>{t.finance.paid}</th>
                  <th>{t.finance.outstanding}</th>
                  <th>{t.finance.status}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((inv) => (
                  <tr key={inv.id}>
                    <td className="ltr-value">
                      {inv.number ?? (inv.provisionalRef ? <span className="badge warn" title={t.sync.temporaryRef}>{inv.provisionalRef}</span> : '—')}
                    </td>
                    <td>{personName(inv.patient)}</td>
                    <td>{inv.issuedAt ? formatDate(inv.issuedAt) : '—'}</td>
                    <td className="ltr-value">{formatMinor(inv.totalMinor, inv.currency, locale)}</td>
                    <td className="ltr-value">{formatMinor(inv.paidMinor, inv.currency, locale)}</td>
                    <td className="ltr-value">
                      <strong style={{ color: inv.outstandingMinor > 0 ? 'var(--warn)' : 'var(--ok)' }}>
                        {formatMinor(inv.outstandingMinor, inv.currency, locale)}
                      </strong>
                    </td>
                    <td>
                      <span className={`badge ${inv.status === 'posted' ? 'ok' : ''}`}>
                        {inv.status === 'posted' ? t.finance.posted : t.finance.draft}
                      </span>
                    </td>
                    <td style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>
                      {inv.status === 'draft' && canPost ? (
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => void post(inv.id)}
                          disabled={busyId === inv.id}
                          title={t.finance.postHint}
                        >
                          {busyId === inv.id ? <Spinner /> : t.finance.post}
                        </button>
                      ) : null}
                      {/* Settling THIS invoice, from the row you are reading —
                          rather than recording a loose payment elsewhere and
                          trusting it to land on the right one. */}
                      {inv.status === 'posted' && inv.outstandingMinor > 0 && canPay ? (
                        <button type="button" className="btn-link" onClick={() => setPaying(inv)}>
                          {t.finance.settleThis}
                        </button>
                      ) : null}
                      {/* Offered only on a draft. A posted invoice has a number
                          in a sequence, and the refusal for that lives in the
                          API — but showing a button that always fails would be
                          a worse way to learn it. */}
                      {inv.status === 'draft' && canDelete ? (
                        <button
                          type="button"
                          className="btn-link danger"
                          onClick={() => setDiscarding(inv)}
                        >
                          {t.remove.delete}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creating ? (
        <NewInvoice
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {paying ? (
        <RecordPayment
          invoice={paying}
          onClose={() => setPaying(null)}
          onDone={() => {
            setPaying(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {discarding ? (
        <ConfirmRemove
          title={t.remove.deleteInvoice}
          what={`${personName(discarding.patient)} · ${formatMinor(discarding.totalMinor, discarding.currency, locale)}`}
          body={<p style={{ margin: 0, color: 'var(--ink-3)' }}>{t.remove.deleteInvoiceBody}</p>}
          actionLabel={t.remove.delete}
          onConfirm={() => api.deleteInvoice(discarding.id)}
          onClose={() => setDiscarding(null)}
          onDone={() => {
            setDiscarding(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * Creating an invoice from unbilled completed treatments.
 *
 * Treatments that already carry a billing source key are shown as
 * "already billed" and cannot be selected — the guard exists in the database
 * too, but the interface should never offer an action that will be refused.
 */
export function NewInvoice({
  initialPatientId,
  onClose,
  onCreated,
}: {
  /** Opened FOR a patient (from the unbilled list): start there, all ticked. */
  initialPatientId?: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t, locale } = useI18n();
  const [, setPatients] = useState<PatientSummary[]>([]);
  const [patientId, setPatientId] = useState(initialPatientId ?? '');
  const [treatments, setTreatments] = useState<TreatmentView[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [installments, setInstallments] = useState('0');
  const [showBlocked, setShowBlocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.patients({ limit: 100 }).then((r) => {
      setPatients(r.patients);
      if (!initialPatientId && r.patients[0]) setPatientId(r.patients[0].id);
    });
  }, [initialPatientId]);

  useEffect(() => {
    if (!patientId) return;
    setSelected(new Set());
    void api.treatments(patientId).then((r) => {
      setTreatments(r.treatments);
      // Arriving from the unbilled list means "invoice all of this", so the
      // chargeable work starts ticked rather than making the user tick each.
      if (initialPatientId) {
        setSelected(
          new Set(r.treatments.filter((x) => x.status === 'completed' && x.billable && !x.billed).map((x) => x.id)),
        );
      }
    });
  }, [patientId, initialPatientId]);

  /*
   * ONE rule, used three times.
   *
   * This used to be two rules: the list below showed every completed
   * treatment, while the total and the submitted lines were computed from
   * completed AND chargeable. Ticking a non-chargeable treatment therefore
   * did nothing to the total and — worse — the line was silently dropped on
   * submit, so you could build an invoice that charged less than what you
   * had ticked, or nothing at all. Anything selectable must be billable, and
   * the money must come from exactly what was selected.
   */
  const invoiceable = treatments.filter((x) => x.status === 'completed' && x.billable && !x.billed);
  const blocked = treatments.filter((x) => x.status === 'completed' && (!x.billable || x.billed));
  const picked = invoiceable.filter((x) => selected.has(x.id));
  const total = picked.reduce((sum, x) => sum + x.priceMinor, 0);
  const currency = invoiceable[0]?.currency ?? blocked[0]?.currency ?? 'USD';

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const lines = picked
        .map((x) => ({
          description: locale === 'ar' ? x.nameAr : x.nameEn,
          treatmentId: x.id,
          quantity: 1,
          unitPriceMinor: x.priceMinor,
        }));
      await api.createInvoice({
        patientId,
        lines,
        installments: Number(installments) > 1 ? Number(installments) : null,
      });
      onCreated();
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t.finance.newInvoice}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button type="button" className="btn" onClick={() => void submit()} disabled={busy || selected.size === 0}>
            {busy ? <><Spinner /> {t.patients.saving}</> : `${t.finance.total}: ${formatMinor(total, currency, locale)}`}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? <div className="notice danger" role="alert">{error}</div> : null}

        <Field label={t.finance.patient} required>
          <PatientPicker value={patientId} onChange={(id) => setPatientId(id)} />
        </Field>

        <div>
          <div className="section-label">{t.finance.selectTreatments}</div>
          {invoiceable.length === 0 ? (
            <p style={{ color: 'var(--ink-3)', marginTop: 8 }}>{t.finance.noUnbilled}</p>
          ) : (
            <div className="stack" style={{ gap: 6, marginTop: 8 }}>
              {invoiceable.map((x) => (
                <label key={x.id} className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
                  <input
                    type="checkbox"
                    checked={selected.has(x.id)}
                    onChange={(e) => {
                      const next = new Set(selected);
                      if (e.target.checked) next.add(x.id);
                      else next.delete(x.id);
                      setSelected(next);
                    }}
                    style={{ marginTop: 3 }}
                  />
                  <span style={{ flex: 1 }}>
                    {locale === 'ar' ? x.nameAr : x.nameEn}
                    {x.toothFdi ? ` · ${t.chart.tooth} ${x.toothFdi}` : ''}
                  </span>
                  <span className="ltr-value">{formatMinor(x.priceMinor, x.currency, locale)}</span>
                </label>
              ))}
            </div>
          )}

          {/*
            Completed work that CANNOT go on an invoice is shown behind a
            disclosure rather than mixed in with tickable rows. Hiding it
            entirely invites "where did that treatment go?"; listing it as a
            dead checkbox is what caused the bug this replaces.
          */}
          {blocked.length > 0 ? (
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                className="btn-link"
                onClick={() => setShowBlocked((v) => !v)}
              >
                {t.finance.hiddenNotChargeable.replace('{n}', String(blocked.length))}{' '}
                {showBlocked ? t.finance.hideNotChargeable : t.finance.showNotChargeable}
              </button>
              {showBlocked ? (
                <div className="stack" style={{ gap: 6, marginTop: 8, opacity: 0.6 }}>
                  {blocked.map((x) => (
                    <div key={x.id} className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
                      <span style={{ flex: 1 }}>
                        {locale === 'ar' ? x.nameAr : x.nameEn}
                        {x.toothFdi ? ` · ${t.chart.tooth} ${x.toothFdi}` : ''}
                        <span className="badge" style={{ marginInlineStart: 8 }}>
                          {x.billed ? t.finance.alreadyBilled : t.finance.notChargeable}
                        </span>
                      </span>
                      <span className="ltr-value">{formatMinor(x.priceMinor, x.currency, locale)}</span>
                    </div>
                  ))}
                  <p className="hint" style={{ color: 'var(--ink-3)', fontSize: 12, margin: 0 }}>
                    {t.finance.notChargeableWhy}
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <Field label={t.finance.installments}>
          <select value={installments} onChange={(e) => setInstallments(e.target.value)}>
            <option value="0">{t.finance.none}</option>
            {[2, 3, 4, 6, 12].map((n) => (
              <option key={n} value={String(n)}>{n}</option>
            ))}
          </select>
        </Field>
      </div>
    </Modal>
  );
}

function Payments({
  clinicId,
  canRecord,
  canVoid,
}: {
  clinicId: string;
  canRecord: boolean;
  canVoid: boolean;
}) {
  const { t, tx, locale, formatDate } = useI18n();
  const [rows, setRows] = useState<Awaited<ReturnType<typeof api.paymentsList>>['payments'] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [reversing, setReversing] = useState<PaymentRow | null>(null);
  const [nonce, setNonce] = useState(0);
  // Filters are applied on the server, so a long history is not shipped to
  // the browser to be searched there.
  const [q, setQ] = useState('');
  const [direction, setDirection] = useState<'' | 'incoming' | 'outgoing'>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [includeReversed, setIncludeReversed] = useState(true);

  useEffect(() => {
    setRows(null);
    const handle = setTimeout(() => {
      api
        .paymentsList(undefined, {
          q: q.trim() || undefined,
          direction: direction || undefined,
          from: from || undefined,
          to: to || undefined,
          includeReversed,
        })
        .then((r) => setRows(r.payments))
        .catch((e) => setError(describe(e, t)));
    }, 250);
    return () => clearTimeout(handle);
  }, [clinicId, nonce, t, q, direction, from, to, includeReversed]);

  if (error) return <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} />;

  return (
    <>
      <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 12 }}>
        {canRecord ? (
          <button type="button" className="btn" onClick={() => setRecording(true)}>
            + {t.finance.recordPayment}
          </button>
        ) : null}
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-body">
          <div className="grid-3">
            <Field label={t.finance.searchPayments}>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.finance.searchPaymentsHint} />
            </Field>
            <Field label={t.finance.direction}>
              <select value={direction} onChange={(e) => setDirection(e.target.value as typeof direction)}>
                <option value="">{t.finance.inAndOut}</option>
                <option value="incoming">{t.finance.moneyIn}</option>
                <option value="outgoing">{t.finance.moneyOut}</option>
              </select>
            </Field>
            <div className="grid-2">
              <Field label={t.transfer.from}>
                <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="ltr-value" />
              </Field>
              <Field label={t.transfer.to}>
                <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="ltr-value" />
              </Field>
            </div>
          </div>
          <label className="row" style={{ gap: 8, marginTop: 8 }}>
            <input type="checkbox" checked={includeReversed} onChange={(e) => setIncludeReversed(e.target.checked)} />
            <span style={{ fontSize: 13 }}>{t.finance.showReversed}</span>
          </label>
        </div>
      </div>

      <div className="card">
        {rows === null ? (
          <Skeleton rows={5} />
        ) : rows.length === 0 ? (
          <EmptyState title={t.finance.noPayments} />
        ) : (
          <div className="card-body flush table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t.finance.issued}</th>
                  <th>{t.finance.direction}</th>
                  <th>{t.finance.counterparty}</th>
                  <th>{t.finance.method}</th>
                  <th>{t.finance.reference}</th>
                  <th>{t.finance.amount}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} style={{ opacity: p.wasReversed ? 0.55 : 1 }}>
                    <td>{formatDate(p.receivedAt)}</td>
                    <td>
                      <span className={p.direction === 'incoming' ? 'badge ok' : 'badge'}>
                        {p.direction === 'incoming' ? t.finance.moneyIn : t.finance.moneyOut}
                      </span>
                    </td>
                    <td>
                      {/* Named, always. "dentist_settlement" told you a
                          dentist was paid; it did not tell you which one. */}
                      {p.counterparty?.kind === 'dentist'
                        ? tx(p.category === 'owner_draw' ? t.finance.ownerDrawFor : t.finance.settlementFor, { name: p.counterparty.name })
                        : p.counterparty?.kind === 'supplier'
                          ? tx(t.finance.supplierPayment, { name: p.counterparty.name })
                          : p.counterparty?.kind === 'laboratory'
                            ? tx(t.finance.laboratoryPayment, { name: p.counterparty.name })
                            : p.counterparty?.name ?? (p.category ?? '—')}
                    </td>
                    <td>{(t.finance.methods as Record<string, string>)[p.method] ?? p.method}</td>
                    <td className="ltr-value">{p.reference ?? '—'}</td>
                    <td className="ltr-value">
                      <strong style={{ color: p.direction === 'incoming' ? 'var(--ok)' : 'var(--ink-2)' }}>
                        {p.direction === 'incoming' ? '+' : '−'}
                        {formatMinor(p.amountMinor, p.currency, locale)}
                      </strong>
                      {/* Both halves of a reversal stay in the list and each
                          says which it is, so the pair reads as one event
                          rather than as two unexplained payments. */}
                      {p.isReversal ? (
                        <span className="badge" style={{ marginInlineStart: 8 }}>
                          {t.remove.reversal}
                        </span>
                      ) : null}
                      {p.wasReversed ? (
                        <span className="badge warn" style={{ marginInlineStart: 8 }}>
                          {t.remove.reversed}
                        </span>
                      ) : null}
                    </td>
                    <td style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>
                      {canVoid && !p.isReversal && !p.wasReversed && p.counterparty?.kind !== 'dentist' ? (
                        <button type="button" className="btn-link danger" onClick={() => setReversing(p)}>
                          {t.remove.voidPayment}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {recording ? (
        <RecordPayment
          onClose={() => setRecording(false)}
          onDone={() => {
            setRecording(false);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {reversing ? (
        <VoidPayment
          payment={reversing}
          onClose={() => setReversing(null)}
          onDone={() => {
            setReversing(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}
    </>
  );
}

type PaymentRow = Awaited<ReturnType<typeof api.paymentsList>>['payments'][number];

/**
 * Reversing a payment.
 *
 * Asks for a reason because the reason is kept, and then reports what the
 * reversal actually cost — specifically, whether commission on this money has
 * already been paid out to a dentist. That number is the one thing about a
 * reversal that surprises people a month later, so it is said here, after the
 * act, in the same breath as the confirmation.
 */
function VoidPayment({
  payment,
  onClose,
  onDone,
}: {
  payment: PaymentRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t, locale, personName } = useI18n();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Awaited<ReturnType<typeof api.voidPayment>> | null>(null);

  if (result) {
    return (
      <Modal
        title={t.remove.voidPayment}
        onClose={onDone}
        footer={
          <button type="button" className="btn" onClick={onDone}>
            {t.common.close}
          </button>
        }
      >
        <div className="stack">
          <div className="notice ok">
            {/* A reversal keeps the payment on record, marked reversed: nothing is deleted. */}
            <span>{t.remove.reversed}.</span>
          </div>
          {result.commissionAlreadySettledMinor > 0 ? (
            <div className="notice danger" role="alert">
              <span>
                {t.remove.commissionWarning.replace(
                  '{amount}',
                  formatMinor(result.commissionAlreadySettledMinor, payment.currency, locale),
                )}
              </span>
            </div>
          ) : null}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={t.remove.voidPayment}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={busy || reason.trim().length < 3}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                setResult(await api.voidPayment(payment.id, reason.trim()));
              } catch (e) {
                setError(describe(e, t));
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? <Spinner /> : t.remove.voidPayment}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? (
          <div className="notice danger" role="alert">
            {error}
          </div>
        ) : null}
        <p style={{ margin: 0 }}>
          <strong>
            {payment.patient ? personName(payment.patient) : (payment.category ?? '—')} ·{' '}
            {formatMinor(payment.amountMinor, payment.currency, locale)}
          </strong>
        </p>
        <p style={{ margin: 0, color: 'var(--ink-3)', fontSize: 13 }}>{t.remove.voidPaymentBody}</p>
        <Field label={t.remove.voidReason} hint={t.remove.voidReasonHint} required>
          <input value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
        </Field>
      </div>
    </Modal>
  );
}

/**
 * Taking money.
 *
 * By default a payment is spread across a patient's open invoices oldest-first,
 * which is the right behaviour when someone hands over a sum against their
 * balance. It is the WRONG behaviour when they are paying for one particular
 * thing — so when this is opened from an invoice row it is aimed at that
 * invoice, pre-filled with what is outstanding on it, and says so.
 */
/**
 * Who is being paid, when the money is going OUT.
 *
 * The same form takes money from a patient and pays a supplier or a lab;
 * the difference is a direction and a counterparty, and pretending they are
 * different screens would mean two places for the idempotency key, the
 * method list and the date to drift apart.
 */
export type PayTo = {
  kind: 'supplier' | 'laboratory';
  id: string;
  name: string;
  outstandingMinor: number;
  currency: string;
  /** Pay THIS bill, not the supplier's whole balance. */
  purchaseInvoiceId?: string;
  purchaseNumber?: string;
  /** Pay for THIS lab order. */
  labOrderId?: string;
};

export function RecordPayment({
  invoice,
  payTo,
  forPatientId,
  onClose,
  onDone,
}: {
  invoice?: InvoiceRow;
  payTo?: PayTo;
  /** Opened from a patient's file: that patient, not a picker. */
  forPatientId?: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t, tx, locale } = useI18n();
  const [, setPatients] = useState<PatientSummary[]>([]);
  const [patientId, setPatientId] = useState(invoice?.patient.id ?? forPatientId ?? '');
  const [amount, setAmount] = useState(() =>
    invoice
      ? minorToInput(invoice.outstandingMinor, invoice.currency)
      : payTo
        ? minorToInput(Math.max(0, payTo.outstandingMinor), payTo.currency)
        : '',
  );
  const [method, setMethod] = useState('cash');
  const cardOnHold = OFFERED_PAYMENT_METHODS.some((m) => m.value === 'card' && !m.available);
  const [reference, setReference] = useState('');
  // Settled by the document being paid, or by the patient's account (which
  // answers in the clinic's currency) once it loads.
  const [currency, setCurrency] = useState(invoice?.currency ?? payTo?.currency ?? 'USD');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [account, setAccount] = useState<Awaited<ReturnType<typeof api.patientAccount>> | null>(null);
  // A stable key per form instance, so a double-click posts ONE receipt (T13).
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  // Money handed over yesterday and entered today belongs to yesterday.
  const [receivedOn, setReceivedOn] = useState(() => new Date().toISOString().slice(0, 10));

  useEffect(() => {
    // An invoice already names its patient; do not overwrite it with whoever
    // happens to be first in the list. An outgoing payment has no patient.
    if (invoice || payTo || forPatientId) return;
    void api.patients({ limit: 100 }).then((r) => {
      setPatients(r.patients);
      if (r.patients[0]) setPatientId(r.patients[0].id);
    });
  }, [invoice, payTo, forPatientId]);

  useEffect(() => {
    if (payTo) {
      setCurrency(payTo.currency);
      return;
    }
    if (!patientId) return;
    void api.patientAccount(patientId).then((a) => {
      setAccount(a);
      setCurrency(a.currency);
    });
  }, [patientId, payTo]);

  async function submit() {
    const minor = parseToMinor(amount, currency);
    if (minor === null || minor <= 0) {
      setError(t.errors.validation);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.recordPayment(
        payTo
          ? {
              direction: 'outgoing',
              supplierId: payTo.kind === 'supplier' ? payTo.id : undefined,
              laboratoryId: payTo.kind === 'laboratory' ? payTo.id : undefined,
              purchaseInvoiceId: payTo.purchaseInvoiceId,
              labOrderId: payTo.labOrderId,
              amountMinor: minor,
              currency,
              method,
              reference: reference || undefined,
              receivedAt: `${receivedOn}T12:00:00.000Z`,
              idempotencyKey,
            }
          : {
              patientId,
              invoiceId: invoice?.id,
              direction: 'incoming',
              amountMinor: minor,
              currency,
              method,
              reference: reference || undefined,
              receivedAt: `${receivedOn}T12:00:00.000Z`,
              idempotencyKey,
            },
      );
      onDone();
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={
        payTo
          ? payTo.kind === 'supplier'
            ? t.finance.paySupplier
            : t.finance.payLaboratory
          : t.finance.recordPayment
      }
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button type="button" className="btn" onClick={() => void submit()} disabled={busy || !amount}>
            {busy ? <><Spinner /> {t.patients.saving}</> : payTo ? t.finance.pay : t.finance.recordPayment}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? <div className="notice danger" role="alert">{error}</div> : null}

        {payTo ? (
          <div className="notice">
            <span>
              {tx(t.finance.payingTo, {
                name: payTo.purchaseNumber ? `${payTo.name} · ${payTo.purchaseNumber}` : payTo.name,
                amount: formatMinor(payTo.outstandingMinor, payTo.currency, locale),
              })}
            </span>
          </div>
        ) : invoice ? (
          /* Aimed at one invoice: the patient is already decided, and saying
             which invoice stops this reading like a loose payment. */
          <div className="notice">
            <span>{tx(t.finance.settlingInvoice, { number: invoice.number ?? '—' })}</span>
          </div>
        ) : forPatientId ? null : (
          <Field label={t.finance.patient} required>
            <PatientPicker value={patientId} onChange={(id) => setPatientId(id)} />
          </Field>
        )}

        {account && account.outstandingMinor > 0 ? (
          <div className="notice">
            <span>
              {t.finance.outstanding}: <strong className="ltr-value">{formatMinor(account.outstandingMinor, account.currency, locale)}</strong>
              {'  '}
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ marginInlineStart: 8 }}
                onClick={() => setAmount(String(account.outstandingMinor / 100))}
              >
                {t.finance.payFull}
              </button>
            </span>
          </div>
        ) : null}

        <div className="grid-3">
          <Field label={t.finance.amount} required>
            <input value={amount} onChange={(e) => setAmount(e.target.value)} className="ltr-value" inputMode="decimal" />
          </Field>
          <Field label={t.settings.currency}>
            <input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} className="ltr-value" maxLength={3} />
          </Field>
          <Field label={t.finance.method} required hint={cardOnHold ? t.finance.cardOnHold : undefined}>
            <select value={method} onChange={(e) => setMethod(e.target.value)}>
              {OFFERED_PAYMENT_METHODS.map((m) => (
                <option key={m.value} value={m.value} disabled={!m.available}>
                  {(t.finance.methods as Record<string, string>)[m.value] ?? m.value}
                  {m.available ? '' : ` — ${t.finance.onHold}`}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid-2">
          <Field label={t.finance.reference} hint={t.patients.optional}>
            <input value={reference} onChange={(e) => setReference(e.target.value)} className="ltr-value" />
          </Field>
          <Field label={t.finance.receivedOn} hint={t.finance.receivedOnHint}>
            <input
              type="date"
              value={receivedOn}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setReceivedOn(e.target.value)}
              className="ltr-value"
            />
          </Field>
        </div>
      </div>
    </Modal>
  );
}

function Expenses({ clinicId, currency }: { clinicId: string; currency: string }) {
  const { t, locale, formatDate } = useI18n();
  const [rows, setRows] = useState<Awaited<ReturnType<typeof api.expenses>>['expenses'] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [nonce, setNonce] = useState(0);
  const [form, setForm] = useState({ category: 'supplies', description: '', amount: '', currency });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setRows(null);
    api.expenses().then((r) => setRows(r.expenses)).catch((e) => setError(describe(e, t)));
  }, [clinicId, nonce, t]);

  async function submit() {
    const minor = parseToMinor(form.amount, form.currency);
    if (minor === null || minor <= 0) return;
    setBusy(true);
    try {
      await api.recordExpense({
        category: form.category,
        description: form.description,
        amountMinor: minor,
        currency: form.currency,
      });
      setAdding(false);
      setForm({ category: 'supplies', description: '', amount: '', currency });
      setNonce((n) => n + 1);
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusy(false);
    }
  }

  if (error) return <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} />;

  return (
    <>
      <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 12 }}>
        <button type="button" className="btn" onClick={() => setAdding(true)}>
          + {t.finance.recordExpense}
        </button>
      </div>

      <div className="card">
        {rows === null ? (
          <Skeleton rows={5} />
        ) : rows.length === 0 ? (
          <EmptyState title={t.finance.noExpenses} />
        ) : (
          <div className="card-body flush table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t.finance.issued}</th>
                  <th>{t.finance.category}</th>
                  <th>{t.finance.description}</th>
                  <th>{t.finance.amount}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => (
                  <tr key={e.id}>
                    <td>{formatDate(e.incurredOn)}</td>
                    <td><span className="badge">{t.finance.expenseCategory[e.category] ?? e.category}</span></td>
                    <td>{e.description}</td>
                    <td className="ltr-value">{formatMinor(e.amountMinor, e.currency, locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {adding ? (
        <Modal
          title={t.finance.recordExpense}
          onClose={() => setAdding(false)}
          footer={
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setAdding(false)}>
                {t.patients.cancel}
              </button>
              <button type="button" className="btn" onClick={() => void submit()} disabled={busy || !form.description || !form.amount}>
                {busy ? <Spinner /> : t.chart.save}
              </button>
            </>
          }
        >
          <div className="stack">
            <div className="grid-2">
              <Field label={t.finance.category} required>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {['rent', 'utilities', 'salaries', 'supplies', 'maintenance', 'marketing', 'other'].map((c) => (
                    <option key={c} value={c}>{t.finance.expenseCategory[c] ?? c}</option>
                  ))}
                </select>
              </Field>
              <Field label={t.finance.amount} required>
                <input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="ltr-value" inputMode="decimal" />
              </Field>
            </div>
            <Field label={t.finance.description} required>
              <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Field>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------ unbilled -- */

/**
 * Work done and never charged for, grouped by patient, with one button that
 * turns a patient's list into an invoice.
 *
 * This is the missing link between the chart and the money: a completed
 * treatment was visible in the patient's record and in the production
 * statistics and invisible to finance until someone remembered. Now it sits
 * in finance, counted, until it is either invoiced or marked not chargeable.
 */
function Unbilled({ clinicId, currency: clinicCurrency, onInvoiced }: { clinicId: string; currency: string; onInvoiced: () => void }) {
  const { t, locale, formatDate, personName } = useI18n();
  const [data, setData] = useState<Awaited<ReturnType<typeof api.unbilled>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [invoicing, setInvoicing] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    setData(null);
    api.unbilled().then(setData).catch((e) => setError(describe(e, t)));
  }, [clinicId, nonce, t]);

  if (error) return <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} />;
  if (!data) return <div className="card"><Skeleton rows={5} /></div>;

  return (
    <>
      <p style={{ color: 'var(--ink-3)', margin: '0 0 12px', fontSize: 13 }}>{t.finance.unbilledHint}</p>

      {data.patients.length === 0 ? (
        <div className="card"><EmptyState title={t.finance.noUnbilled2} /></div>
      ) : (
        <div className="stack">
          <div className="stats">
            <Stat
              label={t.finance.unbilled}
              value={formatMinor(data.totalMinor, data.patients[0]?.currency ?? clinicCurrency, locale)}
              hint={`${data.count} · ${data.patients.length} ${t.patients.title.toLowerCase()}`}
              tone="amber"
            />
          </div>

          {data.patients.map((group) => (
            <div key={group.patient.id} className="card">
              <div className="card-head">
                <h2>
                  {personName(group.patient)}
                  <span className="count-chip">{group.items.length}</span>
                </h2>
                <div className="row" style={{ gap: 12, alignItems: 'center' }}>
                  <strong className="ltr-value">{formatMinor(group.totalMinor, group.currency, locale)}</strong>
                  <button type="button" className="btn btn-sm" onClick={() => setInvoicing(group.patient.id)}>
                    {t.finance.invoiceNow}
                  </button>
                </div>
              </div>
              <div className="card-body flush table-scroll">
                <table>
                  <tbody>
                    {group.items.map((item) => (
                      <tr key={item.id}>
                        <td>{formatDate(item.performedAt)}</td>
                        <td>
                          {locale === 'ar' ? item.nameAr : item.nameEn}
                          {item.toothFdi ? ` · ${t.chart.tooth} ${item.toothFdi}` : ''}
                        </td>
                        <td style={{ color: 'var(--ink-3)' }}>{item.dentistName ?? '—'}</td>
                        <td className="ltr-value" style={{ textAlign: 'end' }}>
                          {formatMinor(item.priceMinor, group.currency, locale)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {invoicing ? (
        <NewInvoice
          initialPatientId={invoicing}
          onClose={() => setInvoicing(null)}
          onCreated={() => {
            setInvoicing(null);
            setNonce((n) => n + 1);
            onInvoiced();
          }}
        />
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------ payables -- */

/**
 * What the clinic owes and to whom, and a button to pay it.
 *
 * Before this, the supplier list knew the supplier balance, the laboratory
 * list knew the lab balance, and finance knew neither — so a bill could be
 * posted and nothing on the money side would say "you owe this". One place,
 * computed from the same facts the ledger was posted from.
 */
function PayablesPanel({ clinicId, currency: clinicCurrency, canPay }: { clinicId: string; currency: string; canPay: boolean }) {
  const { t, tx, locale } = useI18n();
  const [data, setData] = useState<Payables | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState<PayTo | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    setData(null);
    api.payables().then(setData).catch((e) => setError(describe(e, t)));
  }, [clinicId, nonce, t]);

  if (error) return <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} />;
  if (!data) return <div className="card"><Skeleton rows={5} /></div>;

  const currency = data.suppliers[0]?.currency ?? data.laboratories[0]?.currency ?? clinicCurrency;
  const nothing = data.suppliers.length === 0 && data.laboratories.length === 0;

  const rows = (
    kind: 'supplier' | 'laboratory',
    list: { id: string; name: string; currency: string; billedMinor: number; paidMinor: number; outstandingMinor: number }[],
    extra?: (row: { id: string }) => React.ReactNode,
  ) => (
    <table>
      <thead>
        <tr>
          <th>{t.inventory.name}</th>
          <th>{t.finance.billed}</th>
          <th>{t.finance.paid}</th>
          <th>{t.finance.outstanding}</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {list.map((row) => (
          <tr key={row.id}>
            <td>
              <strong>{row.name}</strong>
              {extra ? extra(row) : null}
            </td>
            <td className="ltr-value">{formatMinor(row.billedMinor, row.currency, locale)}</td>
            <td className="ltr-value">{formatMinor(row.paidMinor, row.currency, locale)}</td>
            <td className="ltr-value">
              <strong style={{ color: row.outstandingMinor > 0 ? 'var(--warn)' : 'var(--ok)' }}>
                {formatMinor(row.outstandingMinor, row.currency, locale)}
              </strong>
            </td>
            <td style={{ textAlign: 'end' }}>
              {canPay && row.outstandingMinor > 0 ? (
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() =>
                    setPaying({
                      kind,
                      id: row.id,
                      name: row.name,
                      outstandingMinor: row.outstandingMinor,
                      currency: row.currency,
                    })
                  }
                >
                  {t.finance.pay}
                </button>
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <>
      <p style={{ color: 'var(--ink-3)', margin: '0 0 12px', fontSize: 13 }}>{t.finance.payablesHint}</p>

      <div className="stats">
        <Stat
          label={t.finance.owedToSuppliers}
          hint=""
          value={formatMinor(data.totals.suppliersOutstandingMinor, currency, locale)}
          tone="amber"
        />
        <Stat
          label={t.finance.owedToLabs}
          hint=""
          value={formatMinor(data.totals.laboratoriesOutstandingMinor, currency, locale)}
          tone="amber"
        />
        <Stat
          label={t.finance.pendingAtLab}
          hint=""
          value={formatMinor(data.totals.laboratoriesPendingMinor, currency, locale)}
          tone="blue"
        />
      </div>

      {nothing ? (
        <div className="card"><EmptyState title={t.finance.nothingOwed} /></div>
      ) : (
        <>
          {data.suppliers.length > 0 ? (
            <div className="card">
              <div className="card-head"><h2>{t.inventory.suppliers}</h2></div>
              <div className="card-body flush table-scroll">
                {rows('supplier', data.suppliers, (row) => {
                  const s = data.suppliers.find((x) => x.id === row.id)!;
                  return (
                    <span className="sub" style={{ display: 'block', color: 'var(--ink-3)', fontSize: 12 }}>
                      {tx(t.finance.openBills, { n: s.openBills })}
                    </span>
                  );
                })}
              </div>
            </div>
          ) : null}

          {data.laboratories.length > 0 ? (
            <div className="card">
              <div className="card-head"><h2>{t.lab.laboratories}</h2></div>
              <div className="card-body flush table-scroll">
                {rows('laboratory', data.laboratories, (row) => {
                  const l = data.laboratories.find((x) => x.id === row.id)!;
                  return l.pendingMinor > 0 ? (
                    <span className="sub" style={{ display: 'block', color: 'var(--ink-3)', fontSize: 12 }}>
                      {t.finance.pendingAtLab}: {formatMinor(l.pendingMinor, l.currency, locale)}
                    </span>
                  ) : null;
                })}
              </div>
            </div>
          ) : null}
        </>
      )}

      {paying ? (
        <RecordPayment
          payTo={paying}
          onClose={() => setPaying(null)}
          onDone={() => {
            setPaying(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}
    </>
  );
}
